import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

/**
 * MY-WORK-PRIMITIVE-CONFORMANCE-V6 -- FormControl: THE native-control
 * owners (select, text input) plus the field grammar that binds a label
 * above them, measured from the founder-approved visual reference
 * (docs/design/mockups/my-work-c-system-2026-09-03.html): a 12px/600 label,
 * a 32px control with the firm hairline, hover ink-3, a visible focus ring.
 *
 * The design-system conformance guard's RULE 3 lists this file as an OWNER
 * of the raw <select>; a screen renders selects THROUGH AcSelect and stops
 * being grandfathered. The classes are inert at rest (their look is scoped
 * to the reference in dashboard.css) so an adopter's current frame is
 * byte-identical; DateField stays the date input's owner.
 */
export interface FormFieldProps {
  readonly id: string;
  readonly label: ReactNode;
  readonly children: ReactNode;
  readonly testId?: string;
}

/** Label above control. The caller gives the control the same `id`. */
export function FormField({ id, label, children, testId }: FormFieldProps): JSX.Element {
  return (
    <div className="ac-field" data-testid={testId}>
      <label className="ac-field__label" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

export type AcSelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const AcSelect = forwardRef<HTMLSelectElement, AcSelectProps>(function AcSelect(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={className ? `ac-select ${className}` : 'ac-select'} {...rest}>
      {children}
    </select>
  );
});

export type AcInputProps = InputHTMLAttributes<HTMLInputElement>;

export const AcInput = forwardRef<HTMLInputElement, AcInputProps>(function AcInput(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={className ? `ac-input ${className}` : 'ac-input'} {...rest} />;
});

export default FormField;
